import React from "react";
import { Phone, User, Headset } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

// Small animated "WhatsApp Call / Phone Call -> hub -> Agent / Customer"
// diagram shown on wide screens next to the Call Management setup steps.
// Same bezier-path + <animateMotion> technique as
// pages/connectors/connector/connection-flow.tsx, just with fixed 2-in/2-out
// nodes instead of a data-driven list.
const NODE_H = 56;
const GAP = 40;
const SVG_H = NODE_H * 2 + GAP;
const HUB_Y = SVG_H / 2;
const LEFT_Y = (i: number) => i * (NODE_H + GAP) + NODE_H / 2;

const NodeCard: React.FC<{ icon: React.ReactNode; label: string; sub?: string }> = ({ icon, label, sub }) => (
  <div className="flex items-center gap-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm px-3 py-2 w-32 shrink-0">
    <span className="w-6 h-6 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center shrink-0">{icon}</span>
    <div className="min-w-0">
      <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{label}</div>
      {sub && <div className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">{sub}</div>}
    </div>
  </div>
);

export const MiniCallFlow: React.FC = () => {
  const sources = [
    { id: "wa", icon: <FaWhatsapp size={12} className="text-green-500" />, label: "WhatsApp Call" },
    { id: "ph", icon: <Phone size={12} className="text-indigo-500" />, label: "Phone Call" },
  ];
  const targets = [
    { id: "agent", icon: <Headset size={12} className="text-indigo-500" />, label: "Agent" },
    { id: "cust", icon: <User size={12} className="text-gray-500" />, label: "Customer" },
  ];

  return (
    <div className="flex items-stretch gap-2" style={{ height: SVG_H }}>
      <style>{`
        @keyframes callHubPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.08); }
        }
        .call-hub-pulse { animation: callHubPulse 2.2s ease-in-out infinite; }
      `}</style>

      <div className="flex flex-col justify-between shrink-0">
        {sources.map((s) => (
          <NodeCard key={s.id} icon={s.icon} label={s.label} />
        ))}
      </div>

      <div className="relative w-24 shrink-0" style={{ height: SVG_H }}>
        <svg width="100%" height={SVG_H} viewBox={`0 0 100 ${SVG_H}`} preserveAspectRatio="none" className="absolute inset-0">
          <defs>
            <linearGradient id="miniCallFlowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          {sources.map((s, i) => {
            const y = LEFT_Y(i);
            const id = `mini-src-${s.id}`;
            return (
              <g key={s.id}>
                <path id={id} d={`M 0 ${y} C 25 ${y}, 25 ${HUB_Y}, 50 ${HUB_Y}`} stroke="url(#miniCallFlowGrad)" strokeWidth="1.5" fill="none" strokeDasharray="3 3" />
                <circle r="2" fill="#4f46e5">
                  <animateMotion dur="2.6s" repeatCount="indefinite" begin={`${i * 0.3}s`}>
                    <mpath href={`#${id}`} />
                  </animateMotion>
                </circle>
              </g>
            );
          })}
          {targets.map((t, j) => {
            const y = LEFT_Y(j);
            const id = `mini-dst-${t.id}`;
            return (
              <g key={t.id}>
                <path id={id} d={`M 50 ${HUB_Y} C 75 ${HUB_Y}, 75 ${y}, 100 ${y}`} stroke="url(#miniCallFlowGrad)" strokeWidth="1.5" fill="none" strokeDasharray="3 3" />
                <circle r="2" fill="#4f46e5">
                  <animateMotion dur="2.6s" repeatCount="indefinite" begin={`${1.3 + j * 0.3}s`}>
                    <mpath href={`#${id}`} />
                  </animateMotion>
                </circle>
              </g>
            );
          })}
        </svg>
        <div className="call-hub-pulse absolute left-1/2" style={{ top: HUB_Y }}>
          <div className="w-11 h-11 rounded-full bg-blue-600 shadow-lg flex items-center justify-center">
            <Phone size={18} className="text-white" />
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-between shrink-0">
        {targets.map((t) => (
          <NodeCard key={t.id} icon={t.icon} label={t.label} />
        ))}
      </div>
    </div>
  );
};

export default MiniCallFlow;
